/** One supplier's account page — **EXP-14**, defined by ADR-0063 and bounded by ADR-0040/0109.
 *
 * > *"Show account, submissions, activity, evidence, requests, performance, workflow and history."*
 *
 * ## Every fact here is borrowed, and the page keeps no second copy of any
 *
 * The exchange list, the sharing agreements and the responsiveness counts each have an owner
 * (`useExchange`, `useAgreements`, `useSupplierResponsiveness`), and this page **filters those
 * reads to one supplier** rather than asking the server a differently-shaped question. The cards it
 * draws (`Answer`, `AgreementCard`, `ResponsivenessCard`) are the exchange screen's own, imported,
 * so a supplier cannot read one way here and another there. That is the polymorphic record page's
 * rule — *"the facts are borrowed, never re-rendered"* — applied to a counterparty.
 *
 * ## What the buyer sees is what the agreement lets the supplier's side cross
 *
 * Nothing new crosses ADR-0040's boundary: three tables (request, submission, agreement) and the
 * buyer's own rows. `SHARED_TABLES` is unchanged. The one field added to the wire is the buyer's
 * *own* activity record id, which the buyer already owns.
 *
 * ## Five tabs (Batch 5), and where the old eight went
 *
 * Overview / Requests / Submissions / Agreements / History. Nothing was deleted, only regrouped:
 *
 * - **Overview** holds the old *Account* facts and the old *Performance* (responsiveness) card. EmitIQ
 *   holds **no name** for another company's workspace and this one may not read it; the page says so
 *   rather than inventing a label from a request's free text. *Performance* still means
 *   **responsiveness to requests** and nothing wider (ADR-0063): no emissions trend, no score.
 * - **Requests** holds the old *Requests*, *Activity* (what an answer became in the inventory) and
 *   *Workflow* (links to the records' own work), as three panels. Answering, accepting and revoking
 *   stay on `Suppliers`.
 * - **Submissions** is unchanged.
 * - **Agreements** is the old *Evidence*: the agreement and the submission chain (ADR-0063 point 4) and
 *   **never a supplier document**: whether EmitIQ is a controller or processor of such data is
 *   counsel's (B-02), and `test_nothing_here_stores_a_supplier_document` asserts the column does not
 *   exist. The tab states that boundary instead of rendering an empty panel that reads as a missing feature.
 * - **History** is an ordered account of dated events on rows this page already holds. An
 *   *acceptance* is timed by the copy it made (`accepted_at`, ADR-0150), which only the buyer can
 *   read; the ledger's `supplier.accepted` entry remains the record, and the tab says so.
 *
 * A URL carrying an old tab name (`account`, `performance`, `activity`, `workflow`, `evidence`) still
 * lands on the tab that now holds it, so a shared link does not go blank.
 *
 * ## The header and the strip (Batch 5)
 *
 * - **Engagement status** is derived from the rows and agreements already read, in words with an icon
 *   (design rule 4). It is a statement about *this workspace's dealings*, never a rating of the
 *   supplier. An agreements read that failed or is pending is its own state, not "no agreement".
 * - **Open / answered / overdue** are counts over this workspace's requests. *Overdue* means an open
 *   request with a due date before today (local calendar day) and is zero only when the rows say so.
 * - **Latest submission grade** is the data-quality grade of the figure the latest *accepted* answer
 *   became (`FigureOut.quality`): a submission carries no grade of its own, so a supplier with no
 *   accepted answer reads *Not graded*, never a letter.
 * - **Share of Scope 3** is that supplier's accepted figures over the window's Scope 3 total, **both
 *   summed by the server** (`GET /supplier-exchange/suppliers/{id}/scope3-share`: the inventory's own
 *   rows, both of ADR-0030's exclusions, ADR-0029's allocation, and the window's lock). The page does
 *   not load the figure list and add it up: that stopped working past the client's load ceiling, and
 *   it matched a corrected copy on the first revision's id. The percentage is display arithmetic on
 *   two server totals. No Scope 3 figure in the window reads *Not assessed*, a failed read reads
 *   *Could not be read*, and neither is ever drawn as 0%.
 *
 * ## A supplier this workspace has no exchange with is not told whether it exists
 *
 * An id nothing visible matches reads the same whether the workspace is unknown or merely not one
 * this one deals with — `evidence._document`'s rule, applied to a counterparty.
 *
 * ## One write control here, and it is the Suppliers screen's own (ADR-0151)
 *
 * Answering, accepting and revoking stay on `Suppliers`. **Asking** is also offered on the Requests
 * tab, because the supplier is fixed here and a reader about to chase one has no reason to leave -
 * but it is the same component (`SupplierRaise.tsx`) with the supplier passed in, not a second form,
 * which is the way "a second path to the same write" is kept from becoming two screens that disagree.
 */

import { Link, useParams } from "@tanstack/react-router";

import { useMe } from "../api/auth";
import { useReader } from "../api/profile";
import { useSupplierResponsiveness } from "../api/supplierResponsiveness";
import type { Agreement, Exchange, SupplierScope3Share } from "../api/suppliers";
import { useAgreements, useExchange, useSupplierScope3Share } from "../api/suppliers";
import { KpiTile } from "../components/KpiTile";
import { Loaded, ReadFailed } from "../components/Loaded";
import { PageHeader } from "../components/PageHeader";
import { Empty, Panel } from "../components/Panel";
import type { StatusTone } from "../components/StatusPill";
import { StatusPill } from "../components/StatusPill";
import { instantDate, shortDate, share } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import { RecordPanel, RecordTabs } from "./RecordTabs";
import { ScreenFrame } from "./ScreenFrame";
import { keepSearch } from "./search";
import { AgreementCard, Answer, ResponsivenessCard } from "./SupplierParts";
import { RaiseRequest } from "./SupplierRaise";
import { stateLabel } from "./supplierLabels";
import { SystemDisclosure } from "./SystemDisclosure";
import type { ScreenSearch } from "./search";
import { useScreen } from "./useScreen";
import { useUrlTab } from "./useUrlTab";
import styles from "./Screen.module.css";
import own from "./Suppliers.module.css";
import mine from "./SupplierAccount.module.css";
import rec from "./Record.module.css";

const ACCOUNT_TABS = [
  { key: "overview", label: "Overview" },
  { key: "requests", label: "Requests" },
  { key: "submissions", label: "Submissions" },
  { key: "agreements", label: "Agreements" },
  { key: "history", label: "History" },
] as const;

type TabKey = (typeof ACCOUNT_TABS)[number]["key"];

/** The eight names this page used to have, and the tab that holds what each showed. A link shared
 * before the regrouping must not land on a blank panel. A `Map`, so `constructor` is not a tab. */
const LEGACY_TAB: ReadonlyMap<string, TabKey> = new Map<string, TabKey>([
  ["account", "overview"],
  ["performance", "overview"],
  ["activity", "requests"],
  ["workflow", "requests"],
  ["evidence", "agreements"],
]);

function cls(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

/** Mirrored from `api/suppliers.py`: accepting is an Approver's or an Admin's act. */
const MAY_DECIDE: readonly string[] = ["admin", "approver"];

function what(row: Exchange): string {
  return `${row.fuel_or_carrier.replace(/_/g, " ")} · ${row.category.replace(/_/g, " ")}`;
}

function when(row: Exchange): string {
  return `${shortDate(row.period_start)} – ${shortDate(row.period_end)}`;
}

/** Today as the reader's calendar date, built from local parts: `toISOString()` is UTC and names
 * tomorrow for an evening reader west of Greenwich. */
function localToday(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

/** Open, with a due date that has passed. A request with no due date is never overdue: nobody said
 * when it was due, and the page does not invent a deadline. */
function isOverdue(row: Exchange, today: string): boolean {
  return row.state === "open" && row.due_on !== null && row.due_on < today;
}

/** This workspace's rows with one supplier. `i_am === "buyer"` is the narrowing that matters: a
 * row where this workspace is the *supplier* names the buyer as its counterparty, and listing it
 * under "supplier" would show a customer as a vendor. */
function withSupplier(rows: readonly Exchange[], supplierId: string): Exchange[] {
  return rows.filter((row) => row.i_am === "buyer" && row.supplier_org_id === supplierId);
}

function recordSearch(tab?: string) {
  return (prev: Partial<ScreenSearch>): ScreenSearch =>
    tab ? { ...keepSearch(prev), tab } : keepSearch(prev);
}

/** An agreement that is in force today and lets this workspace copy an answer into its inventory. */
function permitsCopy(agreement: Agreement, today: string): boolean {
  return (
    agreement.revoked_at === null &&
    (agreement.expires_on === null || agreement.expires_on >= today) &&
    agreement.permitted_uses.includes("inventory_copy")
  );
}

interface Standing {
  tone: StatusTone;
  label: string;
  why: string;
}

type AgreementsRead =
  | { status: "pending" }
  | { status: "failed" }
  | { status: "ready"; asBuyer: readonly Agreement[] };

/** Where this workspace stands with the supplier, in words. A statement about the dealings, never a
 * rating of the supplier. Unknown is its own answer: an agreements read that failed must
 * not be reported as "no agreement". */
function standingOf(rows: readonly Exchange[], agreements: AgreementsRead, today: string): Standing {
  if (agreements.status === "failed") {
    return {
      tone: "neutral",
      label: "Standing not known",
      why: "The sharing agreements could not be read, so whether an answer may be copied is not known.",
    };
  }
  if (agreements.status === "pending") {
    return { tone: "neutral", label: "Reading agreements", why: "Reading the sharing agreements." };
  }
  const answered = rows.filter(
    (row) => row.submissions.length > 0 || row.state === "answered" || row.state === "accepted",
  );
  const open = rows.filter((row) => row.state === "open");
  if (answered.length === 0) {
    return open.length > 0
      ? {
          tone: "warn",
          label: "Waiting for an answer",
          why: `${open.length} request${open.length === 1 ? " is" : "s are"} open and none has been answered.`,
        }
      : {
          tone: "neutral",
          label: "No answer",
          why: "Every request to this supplier was declined or withdrawn.",
        };
  }
  return agreements.asBuyer.some((agreement) => permitsCopy(agreement, today))
    ? {
        tone: "ok",
        label: "Engaged",
        why: "Answers have come back and an agreement in force lets this workspace copy them into its inventory.",
      }
    : {
        tone: "warn",
        label: "No agreement permits a copy",
        why: "Answers have come back, and no agreement in force lets this workspace copy one into its inventory.",
      };
}

function SupplierAccountBody() {
  const screen = useScreen();
  const { supplierId } = useParams({ from: "/suppliers/$supplierId" });
  const exchange = useExchange();
  const rows = exchange.data ? withSupplier(exchange.data, supplierId) : [];
  // Read only once there is an account to describe: a read nothing on the page would show the
  // failure of is a read that fails in silence (the screen-states sweep asserts exactly this).
  const agreements = useAgreements(rows.length > 0);
  const [urlTab, selectTab] = useUrlTab(ACCOUNT_TABS);
  const tab: TabKey = LEGACY_TAB.get(screen.tab ?? "") ?? urlTab;

  const today = localToday();
  const read: AgreementsRead = agreements.isError
    ? { status: "failed" }
    : agreements.data
      ? {
          status: "ready",
          asBuyer: agreements.data.filter((a) => a.i_am === "buyer" && a.supplier_org_id === supplierId),
        }
      : { status: "pending" };
  const standing = standingOf(rows, read, today);

  return (
    <ScreenFrame screen={screen} title="Supplier account" crumb="one supplier, as this workspace sees it" bodyScrolls>
      <PageHeader
        title="Supplier workspace"
        subtitle={`Workspace ${supplierId}: what this workspace asked this supplier, and what came back.`}
        tabs={
          rows.length > 0 ? (
            <RecordTabs
              tabs={ACCOUNT_TABS}
              tab={tab}
              onSelect={selectTab}
              label="Supplier account"
              saying={
                <>
                  Showing {ACCOUNT_TABS.find((entry) => entry.key === tab)?.label ?? tab} for supplier
                  workspace {supplierId}.
                </>
              }
            />
          ) : undefined
        }
        actions={
          <>
            {rows.length > 0 ? (
              <StatusPill tone={standing.tone} testId="supplier-engagement">
                {standing.label}
              </StatusPill>
            ) : null}
            <Link className={styles.fixLink} to="/suppliers" search={keepSearch}>
              Back to Suppliers ›
            </Link>
          </>
        }
      />
      {rows.length > 0 ? (
        <>
          <p className={mine.standing} data-testid="supplier-engagement-why">
            {standing.why}
          </p>
          <SupplierFacts rows={rows} supplierId={supplierId} screen={screen} today={today} />
        </>
      ) : null}
      {/* Only while there is nothing to tabulate: a read still loading, a read that failed, or a
          supplier this workspace has no requests with. With rows, the header above holds the tabs
          and this panel is not drawn. */}
      {rows.length === 0 ? (
        <Panel title="Supplier account" sub={`Workspace ${supplierId}`} column="1 / 13">
          <Loaded query={exchange} of="This supplier's exchange">
            {() => (
              // The same sentence for an unknown workspace and one this workspace does not deal
              // with: telling them apart would disclose that a workspace exists.
              <p className={styles.note} data-testid="supplier-account-none">
                This workspace has no requests with that supplier, so there is no account to show.
                An account opens from a row on the Suppliers screen.
              </p>
            )}
          </Loaded>
        </Panel>
      ) : null}

      {rows.length > 0 ? (
        <div className={own.tabArea}>
          <RecordPanel tab={tab}>
            <div className={own.stack}>
              {tab === "overview" ? <OverviewTab supplierId={supplierId} rows={rows} /> : null}
              {tab === "requests" ? <RequestsTab rows={rows} today={today} supplierId={supplierId} /> : null}
              {tab === "submissions" ? <SubmissionsTab rows={rows} /> : null}
              {tab === "agreements" ? <AgreementsTab supplierId={supplierId} rows={rows} /> : null}
              {tab === "history" ? <HistoryTab supplierId={supplierId} rows={rows} /> : null}
            </div>
          </RecordPanel>
        </div>
      ) : null}
    </ScreenFrame>
  );
}

/** The newest accepted row: the one whose answer the inventory last took in. */
function latestAccepted(rows: readonly Exchange[]): Exchange | undefined {
  const lastAnswerAt = (row: Exchange) =>
    row.submissions.reduce((latest, s) => (s.submitted_at > latest ? s.submitted_at : latest), row.created_at);
  return rows
    .filter((row) => row.state === "accepted" && row.accepted_activity_record_id)
    .sort((a, b) => (lastAnswerAt(a) < lastAnswerAt(b) ? 1 : lastAnswerAt(a) > lastAnswerAt(b) ? -1 : 0))[0];
}

interface Tile {
  value: string;
  textual: boolean;
  note: string;
  retry?: () => void;
  /** The calculation whose receipt this tile's figure is, **only when it is exactly one** (rule 3).
   * A share over several figures has no single receipt and says where they are instead. */
  receipt?: string;
}

/** The two tiles that need the window's figures: the grade of the latest accepted answer, and its
 * share of Scope 3. **Both are the server's** (`useSupplierScope3Share`): the page no longer loads
 * the window's figure list and adds it up, which failed past the client's load ceiling and matched
 * a corrected copy on its first revision.
 *
 * **Every state that is not a number says what it is**, and none is drawn as zero: a read still
 * going, a read that failed, a workspace with no reporting period, a Scope 3 with nothing in it and
 * a supplier with nothing accepted are five different claims. */
function figureTiles(
  rows: readonly Exchange[],
  screen: ReturnType<typeof useScreen>,
  read: {
    data: SupplierScope3Share | undefined;
    error: Error | null;
    isPending: boolean;
    refetch: () => unknown;
  },
): { grade: Tile; scope3: Tile } {
  const gradeless = "A submission carries no grade; one is given when an answer is accepted into the inventory.";
  const accepted = latestAccepted(rows);
  const stateless = (value: string, note: string, retry?: () => void): { grade: Tile; scope3: Tile } => ({
    grade: { value: accepted ? value : "Not graded", textual: true, note: accepted ? note : gradeless, retry },
    scope3: { value, textual: true, note, retry },
  });
  if (!screen.window) {
    return screen.isLoading
      ? stateless("Reading", "Reading this workspace's reporting periods.")
      : stateless("No period", "No reporting period is selected, so there is no window to read figures for.");
  }
  if (read.error) {
    return stateless("Could not be read", read.error.message, () => void read.refetch());
  }
  if (read.isPending || !read.data) return stateless("Reading", `Reading figures for ${screen.window.label}.`);

  const found = read.data;

  // Grade: the data-quality grade of the figure the latest accepted answer became.
  const grade: Tile = !accepted
    ? { value: "Not graded", textual: true, note: gradeless }
    : found.latest
      ? {
          value: found.latest.quality,
          textual: false,
          note: `${what(accepted)} · ${when(accepted)}`,
          receipt: found.latest.calculation_id,
        }
      : {
          value: "Not in this period",
          textual: true,
          note: `The latest accepted copy is outside ${screen.window.label}.`,
        };

  let tile: Tile;
  if (found.scope3_figures === 0) {
    tile = { value: "Not assessed", textual: true, note: `No Scope 3 figure in ${screen.window.label}.` };
  } else if (found.supplier_figures === 0) {
    tile = {
      value: "None accepted",
      textual: true,
      note: `No accepted figure from this supplier is in Scope 3 for ${screen.window.label}.`,
    };
  } else {
    const percent = share(found.supplier_kgco2e, found.scope3_kgco2e);
    tile = percent
      ? {
          value: percent,
          textual: false,
          note: `${found.supplier_figures} accepted figure${found.supplier_figures === 1 ? "" : "s"} of ${found.scope3_figures} in Scope 3 for ${screen.window.label}.`,
          receipt: found.supplier_figures === 1 ? (found.only_calculation_id ?? undefined) : undefined,
        }
      : { value: "Not assessed", textual: true, note: `Scope 3 for ${screen.window.label} totals nothing to take a share of.` };
  }
  return { grade, scope3: tile };
}

/** The five tiles. A count of requests has no receipt and is static. The grade and the share are
 * about figures that each have one (rule 3): the grade opens the graded figure's receipt, and the
 * share opens it when exactly one figure makes it up. Over several, the tile is static and links to
 * the Requests tab, where each accepted figure opens its own record. */
function SupplierFacts({
  rows,
  supplierId,
  screen,
  today,
}: {
  rows: readonly Exchange[];
  supplierId: string;
  screen: ReturnType<typeof useScreen>;
  today: string;
}) {
  const shareRead = useSupplierScope3Share(screen.window, screen.method, supplierId, latestAccepted(rows)?.id);
  const count = (state: string) => rows.filter((row) => row.state === state).length;
  const open = count("open");
  const accepted = count("accepted");
  const answered = count("answered") + accepted;
  const overdue = rows.filter((row) => isOverdue(row, today)).length;
  const { grade, scope3 } = figureTiles(rows, screen, shareRead);
  const toRequests = (
    <Link
      className={styles.fixLink}
      to="/suppliers/$supplierId"
      params={{ supplierId }}
      search={recordSearch("requests")}
      data-testid="supplier-account-strip-figures"
    >
      Open the accepted figures ›
    </Link>
  );
  const retry = (tile: Tile) =>
    tile.retry ? (
      <button type="button" className={styles.fixLink} onClick={tile.retry}>
        Try again
      </button>
    ) : undefined;
  return (
    <div className={mine.kpis} role="group" aria-label="This supplier at a glance" data-testid="supplier-account-strip">
      <KpiTile kpi="supplier-open" label="Requests open" value={String(open)} note={`of ${rows.length} sent to this supplier`} />
      <KpiTile
        kpi="supplier-answered"
        label="Requests answered"
        value={String(answered)}
        note={`${accepted} now in your inventory`}
      />
      <KpiTile
        kpi="supplier-overdue"
        label="Requests overdue"
        value={String(overdue)}
        note={overdue > 0 ? "open and past the due date" : "none open past a due date"}
      />
      <KpiTile
        kpi="supplier-grade"
        label="Latest submission grade"
        value={grade.value}
        textual={grade.textual}
        note={grade.note}
        onOpen={grade.receipt ? () => screen.openReceipt(grade.receipt as string) : undefined}
        footer={retry(grade)}
      />
      <KpiTile
        kpi="supplier-scope3-share"
        label="Share of your Scope 3"
        value={scope3.value}
        textual={scope3.textual}
        note={scope3.note}
        onOpen={scope3.receipt ? () => screen.openReceipt(scope3.receipt as string) : undefined}
        footer={scope3.retry ? retry(scope3) : latestAccepted(rows) ? toRequests : undefined}
      />
    </div>
  );
}

function Pair({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <>
      <dt className={cls(rec.k)}>{k}</dt>
      <dd className={cls(rec.v, mono && rec.mono)}>{v}</dd>
    </>
  );
}

function OverviewTab({ supplierId, rows }: { supplierId: string; rows: Exchange[] }) {
  const agreements = useAgreements();
  const response = useSupplierResponsiveness();
  const count = (state: string) => rows.filter((row) => row.state === state).length;
  const live = (agreements.data ?? []).filter(
    (a) => a.i_am === "buyer" && a.supplier_org_id === supplierId && a.revoked_at === null,
  );
  return (
    <>
      <Panel title="Account" column="1 / 13">
        <dl className={cls(rec.kv)} data-testid="supplier-account-facts">
          <Pair k="Supplier workspace" v={supplierId} mono />
          <Pair k="Requests sent" v={String(rows.length)} />
          <Pair k="Waiting for an answer" v={String(count("open"))} />
          <Pair k="Answered" v={String(count("answered"))} />
          <Pair k="In the inventory" v={String(count("accepted"))} />
          <Pair
            k="Agreements not revoked"
            v={
              agreements.isError
                ? "could not be read"
                : agreements.isPending
                  ? "reading…"
                  : String(live.length)
            }
          />
        </dl>
      </Panel>
      <Panel title="Performance" sub="Responsiveness to requests, and nothing wider" column="1 / 13">
        {response.error ? (
          <ReadFailed
            of="Supplier responsiveness"
            error={response.error}
            onRetry={() => void response.refetch()}
          />
        ) : response.isPending ? (
          <p className={styles.note} role="status">
            Reading responsiveness…
          </p>
        ) : (
          (() => {
            const record = (response.data ?? []).find((r) => r.supplier_org_id === supplierId);
            // No record means no request was created for this supplier - not a rate of zero.
            return record ? (
              <ResponsivenessCard row={record} heading={<h3 className={own.title}>Responsiveness</h3>} />
            ) : (
              <p className={styles.note} data-testid="supplier-account-no-requests">
                No requests sent yet.
              </p>
            );
          })()
        )}
      </Panel>
      {/* The honesty copy, kept and ranked: one closed line, every word one click away. */}
      <SystemDisclosure summary="Basis and limits: names, counts and performance" testId="supplier-account-basis">
        <p className={styles.note} data-testid="supplier-account-name-note">
          EmitIQ holds no name for this supplier. A supplier is its own workspace, which this one cannot
          read, so it is identified by the id above rather than by a label taken from a request&rsquo;s
          free text. Counts are over the requests this workspace sent; none is a score.
        </p>
        <p className={styles.note}>
          Here <strong>performance means responsiveness</strong>: how many requests were sent, how many
          were answered and how long the first answer took. It is not an emissions trend and not a
          data-quality score, and nothing here is a rating of the supplier.
        </p>
      </SystemDisclosure>
    </>
  );
}

function SubmissionsTab({ rows }: { rows: Exchange[] }) {
  const reader = useReader();
  const answered = rows.filter((row) => row.submissions.length > 0);
  return (
    <Panel title="Submissions" sub="What the supplier sent, newest request first" column="1 / 13">
      {answered.length === 0 ? (
        <Empty>No answer has come back from this supplier yet.</Empty>
      ) : (
        <ul className={cls(own.list)} data-testid="supplier-account-submissions">
          {answered.map((row) => (
            <li key={row.id} className={cls(own.row, own.rowSingle)} data-request-id={row.id}>
              <span className={cls(own.main)}>
                <span className={cls(own.title)}>{what(row)}</span>
                <span className={cls(own.meta)}>
                  {when(row)} · {stateLabel(row.state)}
                </span>
                {row.submissions.map((submission) => (
                  <span key={submission.id} data-submission-id={submission.id}>
                    <Answer answer={submission} />
                    <span className={cls(own.meta)}>
                      Sent {instantDate(submission.submitted_at, reader)}
                      {submission.supersedes_id ? " · corrects an earlier answer" : ""}
                    </span>
                  </span>
                ))}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function RequestsTab({
  rows,
  today,
  supplierId,
}: {
  rows: Exchange[];
  today: string;
  supplierId: string;
}) {
  return (
    <>
      <Panel title="Requests" sub="What this workspace asked of the supplier" column="1 / 13">
        {/* Asking again is done here with the supplier already fixed (ADR-0151). The same form as the
            Suppliers screen's, so the two cannot disagree about what a request is. */}
        <RaiseRequest rows={rows} supplierId={supplierId} />
        <ul className={cls(own.list)} data-testid="supplier-account-requests">
          {rows.map((row) => (
            <li
              key={row.id}
              className={cls(own.row, own.rowSingle, isOverdue(row, today) && mine.overdueRow)}
              data-request-id={row.id}
            >
              <span className={cls(own.main)}>
                <span className={cls(own.title)}>{what(row)}</span>
                <span className={cls(own.meta, mine.metaLine)}>
                  <span>
                    {when(row)} · {stateLabel(row.state)}
                    {row.due_on ? ` · due ${shortDate(row.due_on)}` : ""}
                  </span>
                  {isOverdue(row, today) ? (
                    <StatusPill tone="warn" testId="supplier-account-overdue">
                      Overdue
                    </StatusPill>
                  ) : null}
                </span>
                {row.note ? <span className={cls(own.note)}>{row.note}</span> : null}
              </span>
            </li>
          ))}
        </ul>
        <p className={styles.note}>
          Answering, accepting and revoking stay on the Suppliers screen, so there is one place each
          of those is done. Asking again is the same form as there, with this supplier already chosen.
        </p>
      </Panel>
      <ActivityPanel rows={rows} />
      <WorkflowPanel rows={rows} />
    </>
  );
}

function ActivityPanel({ rows }: { rows: Exchange[] }) {
  return (
    <Panel
      title="Activity"
      sub="What this supplier's answers became in your inventory"
      column="1 / 13"
    >
      <ul className={cls(own.list)} data-testid="supplier-account-activity">
        {rows.map((row) => (
          <li key={row.id} className={cls(own.row, own.rowSingle)} data-request-id={row.id}>
            <span className={cls(own.main)}>
              <span className={cls(own.title)}>{what(row)}</span>
              <span className={cls(own.meta)}>
                {when(row)} · {stateLabel(row.state)}
              </span>
              {row.accepted_activity_record_id ? (
                <Link
                  className={styles.fixLink}
                  to="/records/$recordId"
                  params={{ recordId: row.accepted_activity_record_id }}
                  search={recordSearch()}
                  data-testid="supplier-account-open-figure"
                >
                  Open the figure in your inventory ›
                </Link>
              ) : row.state === "accepted" ? (
                // Accepted, and the copy is not one this session can see - an engagement narrowed
                // to another period, say. Said, rather than a link to nothing.
                <span className={cls(own.note)}>
                  Accepted into the inventory, but the copy is outside what this session may read.
                </span>
              ) : row.state === "answered" ? (
                <span className={cls(own.note)}>
                  Answered, and not yet copied into the inventory. An Approver decides that on the
                  Suppliers screen.
                </span>
              ) : (
                <span className={cls(own.note)}>Nothing in the inventory comes from this request.</span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function NoAgreement() {
  return (
    <p className={styles.note} data-testid="supplier-account-no-agreement">
      This supplier has granted no agreement to this workspace. An answer can be read and cannot be
      copied into the inventory until it does.
    </p>
  );
}

function AgreementsTab({ supplierId, rows }: { supplierId: string; rows: Exchange[] }) {
  const me = useMe();
  const agreements = useAgreements();
  const mayDecide = me.data ? MAY_DECIDE.includes(me.data.role) : undefined;
  const chain = rows.flatMap((row) => row.submissions);
  return (
    <Panel
      title="Agreements"
      sub="The agreement and the answers - not supplier documents"
      column="1 / 13"
    >
      <Loaded
        query={agreements}
        of="The sharing agreements"
        empty={<NoAgreement />}
      >
        {(all: Agreement[]) => {
          const theirs = all.filter((a) => a.i_am === "buyer" && a.supplier_org_id === supplierId);
          return theirs.length === 0 ? (
            <NoAgreement />
          ) : (
            <ul className={cls(own.agreements)} data-testid="supplier-account-agreements">
              {theirs.map((row) => (
                <AgreementCard key={row.id} row={row} mayDecide={mayDecide} />
              ))}
            </ul>
          );
        }}
      </Loaded>
      <p className={styles.note} data-testid="supplier-account-chain">
        {chain.length} answer{chain.length === 1 ? "" : "s"} across {rows.length} request
        {rows.length === 1 ? "" : "s"}. A supplier&rsquo;s stated basis is the only evidence its
        figure has; the answers are on the Submissions tab.
      </p>
      <p className={styles.note} data-testid="supplier-account-no-documents">
        <strong>A submission carries no document.</strong> Whether EmitIQ is a controller or a
        processor of data a supplier sends is a question for counsel, so evidence here means the
        agreement and the answers. That is a boundary rather than a feature still to come.
      </p>
    </Panel>
  );
}

function WorkflowPanel({ rows }: { rows: Exchange[] }) {
  return (
    <Panel
      title="Workflow"
      sub="The tasks, notes, flags and approvals attached to this supplier's records"
      column="1 / 13"
    >
      <p className={styles.note}>
        Work is attached to records, not to a supplier, and this page does not copy it. Each link opens
        the record with its Workflow tab selected - the same component every other record uses.
      </p>
      <ul className={cls(own.list)} data-testid="supplier-account-workflow">
        {rows.map((row) => (
          <li key={row.id} className={cls(own.row, own.rowSingle)} data-request-id={row.id}>
            <span className={cls(own.main)}>
              <span className={cls(own.title)}>{what(row)}</span>
              <span className={cls(own.meta)}>
                {when(row)} · {stateLabel(row.state)}
              </span>
              <Link
                className={styles.fixLink}
                to="/records/$recordId"
                params={{ recordId: row.id }}
                search={recordSearch("workflow")}
                data-testid="supplier-account-request-workflow"
              >
                Workflow of this request ›
              </Link>
              {row.accepted_activity_record_id ? (
                <Link
                  className={styles.fixLink}
                  to="/records/$recordId"
                  params={{ recordId: row.accepted_activity_record_id }}
                  search={recordSearch("workflow")}
                  data-testid="supplier-account-figure-workflow"
                >
                  Workflow of the figure it became ›
                </Link>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

type Event = { at: string; instant: boolean; text: string };

function eventsOf(rows: Exchange[], agreements: readonly Agreement[]): Event[] {
  const events: Event[] = [];
  for (const row of rows) {
    events.push({ at: row.created_at, instant: true, text: `Asked for ${what(row)}, ${when(row)}.` });
    for (const submission of row.submissions) {
      events.push({
        at: submission.submitted_at,
        instant: true,
        text: `${submission.supersedes_id ? "Corrected an answer for" : "Answered"} ${what(row)}.`,
      });
    }
    // ADR-0150: the instant the copy was made. Null for the supplier's own session and before
    // acceptance, so the line is only ever written from a fact the server returned.
    if (row.accepted_at) {
      events.push({
        at: row.accepted_at,
        instant: true,
        text: `Accepted the answer for ${what(row)} into the inventory.`,
      });
    }
  }
  for (const a of agreements) {
    const uses = a.permitted_uses.map((use) => use.replace(/_/g, " ")).join(", ");
    events.push({ at: a.effective_from, instant: false, text: `Agreement effective, permitting ${uses}.` });
    if (a.expires_on) {
      events.push({ at: a.expires_on, instant: false, text: "Agreement expires." });
    }
    if (a.revoked_at) {
      events.push({
        at: a.revoked_at,
        instant: true,
        text: `Agreement revoked${a.revoked_reason ? `: ${a.revoked_reason}` : ""}. Figures already copied under it stay in the inventory.`,
      });
    }
  }
  // ISO dates and instants both sort lexically; newest first.
  return events.sort((x, y) => (x.at < y.at ? 1 : x.at > y.at ? -1 : 0));
}

function HistoryTab({ supplierId, rows }: { supplierId: string; rows: Exchange[] }) {
  const reader = useReader();
  const agreements = useAgreements();
  return (
    <Panel title="History" sub="Dated events, newest first" column="1 / 13">
      {/* **Wrapped so an empty agreements list is not read as an empty history.** `Loaded` shows
          "Nothing here yet" for an empty array, which hid every ask and answer on this page for a
          workspace that had dealt with the supplier and agreed nothing. The requests are events in
          their own right; the agreements only add to them. */}
      <Loaded query={{ ...agreements, data: agreements.data ? { all: agreements.data } : agreements.data }} of="The sharing agreements">
        {({ all }: { all: Agreement[] }) => {
          const events = eventsOf(
            rows,
            all.filter((a) => a.i_am === "buyer" && a.supplier_org_id === supplierId),
          );
          return (
            <ul className={cls(own.list)} data-testid="supplier-account-history">
              {events.map((event, index) => (
                <li key={`${event.at}-${index}`} className={cls(own.row, own.rowSingle)}>
                  <span className={cls(own.main)}>
                    <span className={cls(own.meta)}>
                      {event.instant ? instantDate(event.at, reader) : shortDate(event.at)}
                    </span>
                    <span>{event.text}</span>
                  </span>
                </li>
              ))}
            </ul>
          );
        }}
      </Loaded>
      <p className={styles.note}>
        The time an answer was <em>accepted</em> into the inventory is the time the copy was made, so
        it is listed here for your own acceptances. The record of the act is the ledger&rsquo;s{" "}
        <code>supplier.accepted</code> entry.
      </p>
    </Panel>
  );
}

export function SupplierAccountScreen() {
  return (
    <RequireSession>
      <SupplierAccountBody />
    </RequireSession>
  );
}
